// Drill pipe inventory, strings, fatigue and twist-offs (M2_SPEC §5, DESIGN §3.4).
//   stock:  state.stock[pipeId] = { qty: joints, wear: average fatigue 0–100 }
//   string: well.string = { defId, joints, fatigue } — assigned at spud, topped up as the hole deepens,
//           returned to stock (fatigue-averaged) when the rig is released.
import { chance } from '../core/rng.js';
import { PIPE, ROTARY } from '../data/m2/rules.js';
import { eqDef, stat, eraDollars, defsByCat, isAvailable } from './defs.js';
import { stockQty, takeStock, addStock } from '../core/state.js';
import { clamp, spend, fmt$, log } from './util.js';

export const JOINT_FT = PIPE.jointFt;
export const jointsFor = (ft) => Math.ceil(Math.max(0, ft) / JOINT_FT - 1e-9);

export function pipeStats(def) {
  return {
    ratedDepth: stat(def, 'ratedDepth', 4000),
    fatiguePerDay: stat(def, 'fatiguePerDay', 1.0),
    twistOffMult: stat(def, 'twistOffMult', 1.0),
  };
}

const pipeDefs = () => defsByCat('pipe');

// Best pipe in stock for a hole of `needFt` (deepest rating first, then the most joints).
export function stockPipeFor(state, needFt = 0) {
  const list = pipeDefs().filter((d) => stockQty(state, d.id) >= 1);
  list.sort((a, b) => (pipeStats(b).ratedDepth >= needFt) - (pipeStats(a).ratedDepth >= needFt) || stockQty(state, b.id) - stockQty(state, a.id));
  return list[0] ?? null;
}

// Make sure the well's string reaches needFt. Returns true when it does.
export function ensureString(state, well, needFt) {
  const need = jointsFor(needFt);
  if (!well.string) {
    const d = stockPipeFor(state, needFt);
    if (!d) return need <= 0;
    well.string = { defId: d.id, joints: 0, fatigue: state.stock[d.id]?.wear ?? 0 };
  }
  const str = well.string;
  if (str.joints >= need) return true;
  const have = Math.floor(stockQty(state, str.defId));
  const take = Math.min(have, need - str.joints);
  if (take > 0) {
    const wear = state.stock[str.defId]?.wear ?? 0;
    str.fatigue = (str.fatigue * str.joints + wear * take) / (str.joints + take);
    takeStock(state, str.defId, take);
    str.joints += take;
  }
  return str.joints >= need;
}

// Put joints back into stock, fatigue-averaged.
export function returnJoints(state, defId, joints, fatigue) {
  if (!defId || joints <= 0) return;
  const s = state.stock[defId];
  const before = s?.qty ?? 0;
  const wear = before > 0 ? ((s.wear ?? 0) * before + fatigue * joints) / (before + joints) : fatigue;
  const ns = addStock(state, defId, joints);
  ns.wear = Math.round(wear * 100) / 100;
}

export function returnString(state, well) {
  const str = well.string;
  if (!str) return;
  returnJoints(state, str.defId, str.joints, str.fatigue);
  well.string = null;
}

// Joints lost in the hole (failed fish, sidetrack).
export function loseJoints(state, well, joints) {
  const str = well.string;
  if (!str) return 0;
  const n = Math.min(str.joints, Math.max(0, joints));
  str.joints -= n;
  return n;
}

// Fatigue added for `rotFrac` of a day rotating.
export function fatigueDay(well, pdef, { rpm = 1, wob = 1, rotFrac = 1 } = {}) {
  const ps = pipeStats(pdef);
  return ps.fatiguePerDay * rpm * wob * (1 + (well.deviationDeg ?? 0) * PIPE.deviationFatigueMult) * rotFrac;
}

// Twist-off chance per rotating day (M2_SPEC §5).
export function twistOffChance(well, pdef, { rpm = 1, h = 3, riskMult = 1, collarsDef = null } = {}) {
  const str = well.string;
  if (!str || !pdef) return 0;
  const ps = pipeStats(pdef);
  const rpmRisk = 1 + ROTARY.rpmRiskSlope * Math.max(0, rpm - ROTARY.rpmRiskKnee) * (h >= 6 ? 1.5 : 1);
  const deep = well.depth > ps.ratedDepth ? 3 : 1;
  const p = Math.max(0, str.fatigue - PIPE.twistOffThreshold) * PIPE.twistOffPerPoint
    * ps.twistOffMult * stat(collarsDef, 'twistOffMult', 1) * riskMult * rpmRisk * deep;
  return clamp(p, 0, 0.5);
}

export function rollTwistOff(state, well, pdef, opts) {
  const p = twistOffChance(well, pdef, opts);
  return p > 0 && !state._rush && chance(state, p);
}

// Inspect pipe in the yard: $ per joint (1925 $), cull the worst, reset fatigue (M2_SPEC §5).
export function inspectCost(state, pipeId) {
  return eraDollars(state, PIPE.inspectCostPerJoint, PIPE.inspectBaseYear) * Math.floor(stockQty(state, pipeId));
}

export function inspectPipeImpl(state, pipeId) {
  const def = eqDef(pipeId);
  if (!def || def.cat !== 'pipe') return { ok: false, msg: 'Not drill pipe.' };
  const s = state.stock[pipeId];
  const qty = Math.floor(s?.qty ?? 0);
  if (qty < 1) return { ok: false, msg: `No ${def.name} in the pipe rack (pipe in a well can't be inspected).` };
  const cost = inspectCost(state, pipeId);
  if (!spend(state, cost, 'consumables')) return { ok: false, msg: `Inspection costs ${fmt$(cost)}.` };
  const cull = Math.min(qty, Math.ceil(qty * PIPE.inspectCullFrac));
  takeStock(state, pipeId, cull);
  if (state.stock[pipeId]) state.stock[pipeId].wear = Math.min(state.stock[pipeId].wear ?? 0, PIPE.inspectResetTo);
  log(state, `Pipe inspection: ${qty} joints of ${def.name} gauged, ${cull} culled.`);
  return { ok: true, msg: `Inspected ${qty} joints of ${def.name} (${fmt$(cost)}): ${cull} culled, the rest reset to ${PIPE.inspectResetTo}% fatigue.` };
}

export const pipeAvailable = (state, def) => isAvailable(def, state);
